import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route } from '~/Constants/Routes.constants';
import { routeReplacer } from '~/Helpers/String.helper';
import { useNavigation, useProvider } from '~/Hooks';
import { useRequestChecklist } from '~/requests/providerService';
import { useLocations } from '~/store/provider';
import { Segment } from '~/Helpers/Segment.helper';
import { SegmentName } from '~/Constants/Segment.constants';
import { useDispatch } from 'react-redux';
import { setProductTour } from '~/store/app/productTours';
import { bindActionCreators } from 'redux';
import { Tour } from '~/Models/interfaces/Tour.enum';
import { CreatedPartnerPortalChecklistStep } from '~/ExportedComponents/TPD/Checklist/types';
import { IChecklistListItem } from '~/ExportedComponents/TPD/Checklist/checklistListItem.model';

const { EVENT_CREATE, PROVIDER_LOCATIONS_EDIT, PROVIDER_LOCATIONS } = Route;

export const useCreatedPartnerPortalChecklist = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const {
    createdPartnerChecklist,
    updateCreatedPartnerChecklist,
    isCheckListLoading,
    checklistError,
    dataUpdatedAt,
  } = useRequestChecklist();
  const { currentProvider } = useProvider();
  const { locationList } = useLocations();
  const setTour = bindActionCreators(setProductTour, dispatch);

  // state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // CONSTANTS
  const domain = currentProvider?.domain || '';

  const locationLink = useMemo(
    () =>
      locationList?.[0]?.id
        ? routeReplacer(PROVIDER_LOCATIONS_EDIT, { domain, id: locationList[0].id })
        : routeReplacer(PROVIDER_LOCATIONS, { domain }),
    [locationList?.[0]?.id],
  );

  const createdPartnerPortalActiveIndex = useMemo(() => {
    if (createdPartnerChecklist) {
      if (createdPartnerChecklist.approvalRequested) {
        return CreatedPartnerPortalChecklistStep.DONE;
      }
      if (createdPartnerChecklist.eventCreated) {
        return CreatedPartnerPortalChecklistStep.REQUEST_APPROVAL;
      }
      if (createdPartnerChecklist.studioDataEdited) {
        return CreatedPartnerPortalChecklistStep.EVENT_CREATED;
      }
      return CreatedPartnerPortalChecklistStep.STUDIO_UPDATED;
    }
    return CreatedPartnerPortalChecklistStep.NO_STEPS;
  }, [createdPartnerChecklist]);

  const createdPartnerPortalChecklistItems: IChecklistListItem[] = useMemo(
    () => [
      {
        index: 1,
        title: t('checklist.partnerPortalCreated.item1.title'),
        description: t('checklist.partnerPortalCreated.item1.description'),
        deactivated: !createdPartnerChecklist?.studioDataEdited,
        active:
          createdPartnerPortalActiveIndex === CreatedPartnerPortalChecklistStep.STUDIO_UPDATED,
        onClick: () => {
          setTour(Tour.STUDIO_CREATION);
          navigate(locationLink);
        },
      },
      {
        index: 2,
        title: t('checklist.partnerPortalCreated.item2.title'),
        description: t('checklist.partnerPortalCreated.item2.description'),
        deactivated: !createdPartnerChecklist?.eventCreated,
        active: createdPartnerPortalActiveIndex === CreatedPartnerPortalChecklistStep.EVENT_CREATED,
        onClick: () => {
          setTour(Tour.EVENT_CREATION);
          navigate(routeReplacer(EVENT_CREATE, { domain }));
        },
      },
    ],
    [createdPartnerPortalActiveIndex, createdPartnerChecklist, domain, locationLink, t],
  );

  useEffect(() => {
    if (checklistError) {
      Segment.track(SegmentName.CHECKLIST_ERRORED);
    }
  }, [checklistError]);

  useEffect(() => {
    if (dataUpdatedAt) {
      Segment.track(SegmentName.CHECKLIST_CALLED);
    }
  }, [dataUpdatedAt]);

  return {
    createdPartnerPortalActiveIndex,
    createdPartnerPortalChecklistItems,
    updateCreatedPartnerChecklist,
    isCheckListLoading,
    checklistError,
    createdPartnerChecklist,
    isModalOpen,
    setIsModalOpen,
  };
};
